/**
 * Manual del Chofer — Módulo de Gastos (someter recibos).
 * Verified behavior: el Chofer (rol `employee`) solo somete recibos en Gastos.
 * Contenido derivado del código real: LoginPage, EmployeePortal, HistoryPage,
 * UploadPage, ReceiptDetailPage y shared/constants.
 */
export default {
  roleKey: 'employee',
  roleLabel: 'Chofer',
  icon: '🧾',
  subtitle: 'Módulo de Gastos — subir, enviar y dar seguimiento a sus recibos.',
  sections: [
    {
      id: 'iniciar-sesion',
      group: 'Primeros pasos',
      title: 'Iniciar sesión en el portal',
      blocks: [
        { type: 'paragraph', text: 'El portal V&A usa su cuenta de Microsoft de la empresa para iniciar sesión.' },
        { type: 'steps', items: [
          'Abra el portal V&A en su navegador.',
          'Pulse el botón "Iniciar sesión con Microsoft" e ingrese con su cuenta de la empresa.',
          'Al entrar verá su pantalla principal: "Mis Reportes de Gastos".',
        ] },
        { type: 'note', variant: 'info', text: 'Si su cuenta no es de Microsoft, pulse la opción de correo y contraseña, escriba sus datos y pulse "Iniciar sesión".' },
        { type: 'note', variant: 'warning', text: 'Si aparece "Tu sesión de Microsoft expiró. Vuelve a iniciar sesión.", vuelva a pulsar "Iniciar sesión con Microsoft". Si dice "Correo o contraseña incorrectos", verifique sus datos o contacte al administrador.' },
      ],
    },
    {
      id: 'mis-recibos',
      group: 'Gastos',
      title: 'Pantalla principal — Mis Reportes de Gastos',
      blocks: [
        { type: 'paragraph', text: 'La pantalla "Mis Reportes de Gastos" muestra todos sus recibos, agrupados por estado. Arriba verá tarjetas de resumen (Total, Borradores, En Revisión, Devueltos, Aprobados, Rechazados); puede pulsarlas para filtrar.' },
        { type: 'subheading', text: 'Qué significa cada estado' },
        { type: 'list', items: [
          'Borrador — guardado, todavía no enviado.',
          'Pendiente / Pendiente de Revisión — enviado y en proceso de aprobación.',
          'Devuelto / Parcialmente Devuelto — le fue regresado para que lo corrija.',
          'Aprobado — aprobado por la empresa.',
          'Pagado — ya fue pagado.',
          'Rechazado — no fue aceptado (vea el motivo).',
        ] },
        { type: 'steps', items: [
          'Cada tarjeta muestra el número de recibo, proveedor, fecha, método de pago y la etiqueta de estado.',
          'Pulse "Ver artículos (N)" para ver las partidas del recibo y su estado.',
          'Pulse la imagen en miniatura para ver la foto del recibo en grande.',
          'Use los filtros de estado, fecha y proyecto para encontrar un recibo.',
        ] },
        { type: 'note', variant: 'info', text: 'Para crear un recibo nuevo, pulse "+ Nuevo Gasto" (arriba a la derecha). En el teléfono, use las pestañas inferiores: Reportes, Nuevo y Borradores.' },
      ],
    },
    {
      id: 'subir-recibo',
      group: 'Gastos',
      title: 'Subir un recibo nuevo (foto + IA)',
      blocks: [
        { type: 'steps', items: [
          'En "Mis Reportes de Gastos" pulse "+ Nuevo Gasto".',
          'Pulse el recuadro "Subir recibo" (o arrastre la imagen hasta él) y seleccione la foto o el PDF del recibo.',
          'Espere mientras aparece "Analizando recibo…": la IA lee el recibo y autocompleta el proveedor, la fecha, el número de factura, el subtotal y las partidas.',
        ] },
        { type: 'note', variant: 'info', text: 'Se aceptan fotos JPG, PNG, GIF, WEBP, archivos PDF y fotos de iPhone (HEIC). El tamaño máximo es 10 MB.' },
        { type: 'note', variant: 'info', text: 'La IA es solo una ayuda. Junto a los datos verá "IA: ___ confianza". Siempre revise que la información coincida con el recibo y corrija lo que haga falta.' },
        { type: 'note', variant: 'warning', text: 'Si la IA no logra leer el recibo o deja campos vacíos, no hay problema: escriba usted mismo los datos y las partidas. También puede subir la foto y llenar todo a mano.' },
      ],
    },
    {
      id: 'completar-datos',
      group: 'Gastos',
      title: 'Completar datos y partidas',
      blocks: [
        { type: 'subheading', text: 'Datos del recibo' },
        { type: 'list', items: [
          'Proveedor — obligatorio.',
          'Fecha — obligatoria. No se permiten fechas futuras.',
          'Número de Factura — opcional.',
          'Método de Pago — obligatorio: "Tarjeta de Crédito" o "Petty Cash". Si elige Tarjeta de Crédito, seleccione además la tarjeta (American Express, Home Depot, Mastercard, Total o Visa).',
          'Subtotal del Recibo (marcado con ✓) — escriba el subtotal SIN impuestos. Los montos de las partidas deben sumar este subtotal.',
        ] },
        { type: 'subheading', text: 'Partidas (Items del Recibo)' },
        { type: 'steps', items: [
          'Para cada partida, elija el tipo: "Proyecto" o "Administrativo" (esto filtra la lista de proyectos disponibles).',
          'Seleccione el Proyecto. El Supervisor se asigna automáticamente según el proyecto — no se escribe a mano.',
          'Escriba la Descripción de la partida.',
          'Ingrese la Cantidad y el Precio Unitario (el Monto se calcula solo), o escriba el Monto directamente.',
          'Use "Agregar Item" para añadir más partidas, o la "X" para eliminar una.',
        ] },
        { type: 'note', variant: 'info', text: 'Si todas las partidas son del mismo proyecto, marque "Mismo proyecto para todos" y elija el proyecto una sola vez. De igual forma, "Mismo tipo para todos" aplica el tipo (Proyecto/Administrativo) a todas. Marque "Personal" en una partida si es un gasto personal y no del proyecto.' },
        { type: 'note', variant: 'warning', text: 'Si el Supervisor no se llena solo al elegir el proyecto, ese proyecto no tiene un supervisor asignado y no podrá enviarlo. Contacte al administrador (usted no puede seleccionar el supervisor a mano).' },
        { type: 'note', variant: 'warning', text: 'Si el recuadro del Subtotal se pone rojo o aparece "El subtotal de los items no coincide con el subtotal del recibo", revise que estén todas las partidas y que sus montos sumen el Subtotal del Recibo (se permite una diferencia de hasta $0.02). El botón "Agregar Item" se desactiva cuando los montos ya cuadran.' },
      ],
    },
    {
      id: 'guardar-borrador',
      group: 'Gastos',
      title: 'Guardar como borrador',
      blocks: [
        { type: 'paragraph', text: 'Si todavía no quiere enviar el recibo, puede guardarlo como borrador y terminarlo después.' },
        { type: 'steps', items: [
          'Pulse "Guardar Borrador". El recibo queda en la sección "Borradores" con la etiqueta "Borrador".',
          'Para retomarlo, ábralo desde Borradores (o desde su tarjeta) y pulse "Abrir".',
          'Cuando lo termine, podrá guardarlo de nuevo o enviarlo para revisión.',
        ] },
        { type: 'note', variant: 'info', text: 'Si pulsa "Regresar" con cambios sin guardar, el sistema le preguntará si desea guardar como borrador, descartar los cambios o seguir editando.' },
        { type: 'note', variant: 'warning', text: 'Desde un borrador puede pulsar "Eliminar Borrador" para borrarlo; esta acción no se puede deshacer. Solo los borradores se pueden eliminar.' },
      ],
    },
    {
      id: 'enviar-revision',
      group: 'Gastos',
      title: 'Enviar para revisión',
      blocks: [
        { type: 'steps', items: [
          'Cuando todo esté completo y el subtotal cuadre, pulse "Enviar Reporte".',
          'El sistema valida que haya una foto del recibo; que estén el Proveedor, la Fecha y el Método de Pago; que haya al menos una partida con Proyecto, Descripción, Monto y Supervisor; y que las partidas sumen el subtotal.',
          'Si todo está correcto, el recibo pasa a "Pendiente" y se envía al Ingeniero(a) del proyecto para su revisión.',
        ] },
        { type: 'note', variant: 'warning', text: 'Si falta algún dato, aparecerá un aviso indicando qué campo corregir (por ejemplo: "completa todos los campos de cada item — Proyecto, Descripción, Monto, Supervisor").' },
        { type: 'note', variant: 'warning', text: 'Si aparece "Ya existe un recibo similar con la misma fecha y monto", revise que no esté enviando un duplicado. Confirme solo si está seguro de que es un recibo distinto.' },
        { type: 'note', variant: 'success', text: 'Al enviarse verá el mensaje "1 recibo enviado para revisión" y volverá a "Mis Reportes de Gastos".' },
      ],
    },
    {
      id: 'consultar-estado',
      group: 'Gastos',
      title: 'Consultar el estado de un recibo',
      blocks: [
        { type: 'steps', items: [
          'En "Mis Reportes de Gastos", ubique la tarjeta del recibo; su estado aparece en la etiqueta de color.',
          'Pulse "Ver artículos" para ver cada partida con su estado individual.',
          'Pulse la tarjeta para abrir el detalle y ver el avance de la aprobación.',
        ] },
        { type: 'note', variant: 'info', text: 'Si una tarjeta dice "Tiene comentarios del supervisor — expandir items para ver", pulse "Ver artículos" para leer el motivo y las notas del revisor.' },
      ],
    },
    {
      id: 'corregir-devuelto',
      group: 'Gastos',
      title: 'Corregir un recibo devuelto',
      blocks: [
        { type: 'paragraph', text: 'Si un revisor le regresa un recibo, este aparece en la sección "Devueltos" con la etiqueta "Devuelto" (o "Parcialmente Devuelto" si solo regresaron algunas partidas).' },
        { type: 'steps', items: [
          'Pulse el botón ámbar "Editar" en la tarjeta devuelta (o abra el recibo). Se abrirá en modo edición.',
          'Lea el aviso "Devuelto:" en la parte superior — ahí aparece el motivo y las notas del revisor.',
          'Corrija lo que le indicaron. Si es "Parcialmente Devuelto", solo podrá editar las partidas devueltas; las demás aparecen como "no editables".',
          'Verifique que el subtotal siga cuadrando y pulse "Reenviar para Revisión".',
        ] },
        { type: 'note', variant: 'warning', text: 'Un recibo devuelto no se puede eliminar: solo se corrige y se reenvía.' },
        { type: 'note', variant: 'success', text: 'Al reenviarlo verá el mensaje "Recibo reenviado para revisión".' },
      ],
    },
  ],
};
